import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Key, Copy, Check, Trash2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { format } from '../../utils/dateUtils';

import config from '../../config';
const API = `${config.BACKEND_URL}/api`;

const SCOPES = {
  tickets: { label: 'Tickets only', description: 'Can only create tickets. Use this for keys built into external apps.' },
  full: { label: 'Full access', description: 'Admin access to all endpoints. Keep these keys private.' },
};

export const AdminApiTokens = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [form, setForm] = useState({ name: '', scope: 'tickets' });

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const response = await axios.get(`${API}/api-tokens`);
      setTokens(response.data);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      toast.error('Failed to load API tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api-tokens`, form);
      setNewToken(response.data);
      setForm({ name: '', scope: 'tickets' });
      fetchTokens();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API token');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this API token? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api-tokens/${id}`);
      toast.success('API token deleted');
      fetchTokens();
    } catch (error) {
      toast.error('Failed to delete API token');
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await axios.put(`${API}/api-tokens/${id}/toggle`);
      toast.success(response.data.message);
      fetchTokens();
    } catch (error) {
      toast.error('Failed to toggle API token');
    }
  };

  const copyToClipboard = () => {
    if (newToken?.token) {
      navigator.clipboard.writeText(newToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Token copied to clipboard');
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setNewToken(null);
      setForm({ name: '', scope: 'tickets' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-api-tokens-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">API Tokens</h1>
          <p className="text-muted-foreground mt-1">Manage API access tokens for external integrations.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-token-btn">
              <Plus className="h-4 w-4" />
              New Token
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{newToken ? 'Token Created' : 'Create API Token'}</DialogTitle>
            </DialogHeader>
            
            {newToken ? (
              <div className="space-y-4 mt-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Important</AlertTitle>
                  <AlertDescription>
                    Copy this token now. You will not be able to see it again!
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label>Your API Token</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newToken.token}
                      readOnly
                      className="font-mono text-sm"
                      data-testid="new-token-value"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyToClipboard}
                      data-testid="copy-token-btn"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => handleDialogClose(false)} data-testid="close-token-dialog-btn">
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Token Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., CI/CD Pipeline"
                    required
                    data-testid="token-name-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Give your token a descriptive name to identify its purpose.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Access</Label>
                  <Select value={form.scope} onValueChange={(value) => setForm({ ...form, scope: value })}>
                    <SelectTrigger data-testid="token-scope-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SCOPES).map(([value, scope]) => (
                        <SelectItem key={value} value={value}>{scope.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{SCOPES[form.scope].description}</p>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="token-submit-btn">Create Token</Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">API Usage Guide</CardTitle>
          <CardDescription>
            Send the API key in the <code className="bg-muted px-1 py-0.5 rounded text-xs">X-API-Key</code> header.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted p-4 rounded-lg font-mono text-sm overflow-x-auto">
            <p className="text-muted-foreground mb-2"># Create a ticket (works with "Tickets only" keys)</p>
            <p>curl -X POST -H "X-API-Key: YOUR_TOKEN" -H "Content-Type: application/json" \</p>
            <p className="pl-4">-d '{"{"}"customer_name": "Jane Doe", "app_name": "My App", "type": "bug", "description": "...", "email": "jane@example.com"{"}"}' \</p>
            <p className="pl-4">{config.BACKEND_URL}/api/tickets</p>
            <p className="text-muted-foreground mt-4 mb-2"># Other endpoints (require "Full access" keys)</p>
            <p>curl -H "X-API-Key: YOUR_TOKEN" \</p>
            <p className="pl-4">{config.BACKEND_URL}/api/projects</p>
            <p className="mt-2">curl -H "X-API-Key: YOUR_TOKEN" \</p>
            <p className="pl-4">{config.BACKEND_URL}/api/user-stories</p>
          </div>
          
          <div className="space-y-2">
            <p className="text-sm font-medium">Access Levels:</p>
            <p className="text-sm text-muted-foreground">
              <Badge variant="outline" className="mr-1">{SCOPES.tickets.label}</Badge>
              keys can only call <code className="text-xs">POST /api/tickets</code>. Fields: <code className="text-xs">customer_name</code> and{' '}
              <code className="text-xs">description</code> (max 2000 characters) are required; <code className="text-xs">app_name</code>,{' '}
              <code className="text-xs">type</code> (question, bug or remark), <code className="text-xs">email</code> and{' '}
              <code className="text-xs">phone</code> are optional.
            </p>
            <p className="text-sm text-muted-foreground">
              <Badge variant="secondary" className="mr-1">{SCOPES.full.label}</Badge>
              keys grant admin access to all endpoints in the API documentation.
            </p>
          </div>
          
          <div className="space-y-2">
            <p className="text-sm font-medium">Common Endpoints:</p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">POST</Badge>
                <code className="text-muted-foreground">/api/tickets</code>
                <span className="text-muted-foreground">- Create ticket</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">GET</Badge>
                <code className="text-muted-foreground">/api/projects</code>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">GET</Badge>
                <code className="text-muted-foreground">/api/user-stories</code>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">GET</Badge>
                <code className="text-muted-foreground">/api/releases</code>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">GET</Badge>
                <code className="text-muted-foreground">/api/customers</code>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">POST</Badge>
                <code className="text-muted-foreground">/api/projects</code>
                <span className="text-muted-foreground">- Create project</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tokens List */}
      {tokens.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="empty-state">
              <Key className="empty-state-icon" />
              <p className="empty-state-title">No API tokens</p>
              <p className="empty-state-description">
                Create an API token to enable external system access.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Access</th>
                  <th>Status</th>
                  <th className="hidden md:table-cell">Created</th>
                  <th className="w-28">Active</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id} data-testid={`token-row-${token.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <Key className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{token.name}</span>
                      </div>
                    </td>
                    <td>
                      <Badge variant={token.scope === 'tickets' ? 'outline' : 'secondary'}>
                        {SCOPES[token.scope]?.label || token.scope}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant={token.active ? 'default' : 'secondary'}>
                        {token.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="hidden md:table-cell text-muted-foreground text-sm">
                      {format(new Date(token.created_at), 'MMM d, yyyy')}
                    </td>
                    <td>
                      <Switch
                        checked={token.active}
                        onCheckedChange={() => handleToggle(token.id)}
                        data-testid={`toggle-token-${token.id}`}
                      />
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDelete(token.id)}
                        data-testid={`delete-token-${token.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
